import {
  Alert,
  Anchor,
  Badge,
  Button,
  Flex,
  Group,
  Menu,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import {
  IconCalendar,
  IconChevronDown,
  IconFile,
  IconInfoCircle,
  IconLock,
  IconLockOpen,
  IconMessage,
  IconMessageOff,
  IconMinus,
  IconPlus,
  IconShare,
  IconTrash,
  IconUsers,
  IconUsersGroup,
  IconWand,
} from "@tabler/icons-react";
import { addDays, format, formatISO, parseISO } from "date-fns";
import { useState } from "react";
import { Redirect, useParams } from "wouter";
import { client } from "@/api-client";
import type {
  GroupClassTypePrivateOutput,
  GroupStatus,
  GroupTrainerOutput,
} from "@/api-types";
import { CreateGroupForm } from "@/components/CreateGroupForm";
import { EditGroupForm } from "@/components/EditGroupForm";
import { FancyLoader } from "@/components/FancyLoader";
import { GroupCard } from "@/components/GroupCard";
import { GroupFiles } from "@/components/GroupFiles";
import { GroupParticipants, RequestCard } from "@/components/GroupParticipants";
import { Header } from "@/components/Header";
import { useHashTab } from "@/hooks/useHashTab";
import {
  createPath,
  TrainerRoutes,
  UniversalRoutes,
  useNavigator,
} from "@/routers/routes";
import { getGroupStatusColor, getGroupStatusDisplayText } from "@/utils/groups";

// Type aliases for direct imports from @/api-types

const EmptyGroupsState = ({ onCreateGroup }: { onCreateGroup: () => void }) => (
  <Stack align="center" py="xl">
    <IconUsersGroup size={64} color="gray" />
    <Stack align="center" gap="xs">
      <Title order={3}>No groups yet</Title>
      <Text c="dimmed" ta="center">
        Create your first group to start organizing group training sessions
      </Text>
    </Stack>
    <Button leftSection={<IconPlus size={16} />} onClick={onCreateGroup}>
      Create Group
    </Button>
  </Stack>
);

type GroupFilter = "ACTIVE" | "OPEN" | "LOCKED" | "INACTIVE";

export const Groups = () => {
  const {
    data: groups,
    isLoading,
    refetch,
  } = client.useQuery("get", "/trainer/groups/");
  const [filter, setFilter] = useState<GroupFilter>("ACTIVE");

  if (isLoading || !groups) {
    return <FancyLoader />;
  }

  const filteredGroups = groups.groups.filter((group) => {
    if (filter === "ACTIVE") {
      return group.status !== "INACTIVE";
    } else if (filter === "OPEN") {
      return group.status === "OPEN" || group.status === "OPEN_VIA_LINK";
    } else {
      return group.status === filter;
    }
  });

  const openCreateModal = () => {
    modals.open({
      title: "Create Group",
      children: (
        <CreateGroupForm
          onClose={() => modals.closeAll()}
          onSuccess={() => {
            modals.closeAll();
            refetch();
          }}
        />
      ),
      size: "lg",
    });
  };

  return (
    <Stack>
      <Flex justify="space-between" align="center">
        <Stack gap="xs">
          <Title order={1}>Groups</Title>
        </Stack>
        <Button
          rightSection={<IconPlus />}
          variant="outline"
          onClick={openCreateModal}
        >
          Create Group
        </Button>
      </Flex>

      {groups.groups.length === 0 ? (
        <EmptyGroupsState onCreateGroup={openCreateModal} />
      ) : (
        <Stack>
          <Tabs
            value={filter}
            onChange={(value) => value && setFilter(value as GroupFilter)}
          >
            <Tabs.List grow>
              <Tabs.Tab value="ACTIVE">Active</Tabs.Tab>
              <Tabs.Tab value="OPEN">Open</Tabs.Tab>
              <Tabs.Tab value="LOCKED">
                {getGroupStatusDisplayText("LOCKED")}
              </Tabs.Tab>
              <Tabs.Tab value="INACTIVE">
                {getGroupStatusDisplayText("INACTIVE")}
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Stack>
            {filteredGroups.map((group) => (
              <GroupCard key={group.id} group={group} />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const GroupSessionsDisplay = ({
  group,
  groupClassType,
}: {
  group: GroupTrainerOutput;
  groupClassType: GroupClassTypePrivateOutput;
}) => {
  const { navigator } = useNavigator();
  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Text size="sm">
            <strong>Class Type:</strong>{" "}
            <Anchor
              component="button"
              onClick={navigator({
                path: TrainerRoutes.PROFILE.GROUP_CLASS_TYPE_EDIT,
                params: { groupClassTypeId: groupClassType.id },
              })}
            >
              {groupClassType.name}
            </Anchor>
          </Text>
          <Text size="sm">
            <strong>Total Price:</strong> €
            {groupClassType.pricePer === "SESSION"
              ? ((groupClassType.price / 100) * group.sessions.length).toFixed(
                  2,
                )
              : (groupClassType.price / 100).toFixed(2)}
          </Text>
        </Stack>
      </Paper>

      <Text size="lg" fw={600}>
        Group Sessions
      </Text>
      {group.sessions.map((session) => (
        <Paper key={session.id} p="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text fw={500}>
                {groupClassType.name} {session.id}
              </Text>
              <Text size="sm" c="dimmed">
                {format(parseISO(session.scheduledAt), "PPP 'at' p")}
              </Text>
            </div>
            <Badge color={session.status === "CANCELLED" ? "orange" : "green"}>
              {session.status}
            </Badge>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
};

const GroupSessionsAssignment = ({
  group,
  refetch,
}: {
  group: GroupTrainerOutput;
  refetch: () => void;
}) => {
  const { data: groupClassTypes } = client.useQuery(
    "get",
    "/trainer/profile/group-class-types",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );
  const createSessionsMutation = client.useMutation(
    "post",
    "/trainer/groups/{groupId}/sessions",
  );
  const form = useForm({
    initialValues: {
      groupClassTypeId: "",
      sessions: [] as { scheduledAt: string | null }[],
    },
    validate: {
      groupClassTypeId: (value) =>
        !value ? "Please select a group class type" : null,
      sessions: {
        scheduledAt: (value) => (!value ? "Please set session date" : null),
      },
    },
  });
  const selectedType = groupClassTypes?.find(
    (t) => t.id === form.values.groupClassTypeId,
  );
  const handleTypeSelect = (typeId: string) => {
    form.setFieldValue("groupClassTypeId", typeId);
    const type = groupClassTypes?.find((t) => t.id === typeId);
    if (type) {
      const newSessions = Array.from({ length: type.sessionCount }, () => ({
        scheduledAt: null,
      }));
      form.setFieldValue("sessions", newSessions);
    }
  };
  const handleConfirmAssignment = async () => {
    try {
      const sessionDatetimes = form.values.sessions
        .map((s) => s.scheduledAt)
        .filter(Boolean)
        .map((dateStr) => {
          const date = parseISO(dateStr as string);
          return formatISO(date);
        });
      if (sessionDatetimes.length !== form.values.sessions.length) {
        alert("Please set all session dates");
        return;
      }
      await createSessionsMutation.mutateAsync({
        params: { path: { groupId: group.id } },
        body: {
          groupClassTypeId: form.values.groupClassTypeId,
          sessionsScheduledAt: sessionDatetimes,
        },
      });
      refetch();
    } catch (error) {
      console.error("Failed to create sessions:", error);
    }
  };
  const firstScheduledAt = form.values.sessions[0]?.scheduledAt;
  const handleScheduleWeekly = () => {
    if (!firstScheduledAt) {
      return;
    }
    form.values.sessions.forEach((_, index) => {
      if (index === 0) return;
      const firstDate = parseISO(firstScheduledAt);
      const newDate = addDays(firstDate, index * 7);
      form.setFieldValue(
        `sessions.${index}.scheduledAt`,
        newDate.toISOString(),
      );
    });
  };
  const handleAddSession = () => {
    form.insertListItem("sessions", { scheduledAt: null });
  };
  const handleRemoveSession = (index: number) => {
    if (form.values.sessions.length <= 1) return;
    form.removeListItem("sessions", index);
  };
  const calculateTotalPrice = () => {
    if (!selectedType) return 0;
    if (selectedType.pricePer === "SESSION") {
      return (selectedType.price / 100) * form.values.sessions.length;
    } else {
      return selectedType.price / 100;
    }
  };
  const openAssignmentModal = () => {
    modals.openConfirmModal({
      title: "Confirm Class Type Assignment",
      children: (
        <Stack>
          <Alert color="orange" icon={<IconInfoCircle size={16} />}>
            Once you assign a class type to this group, it cannot be changed.
            Are you sure you want to proceed? Potential participants will see
            this information and based on that they ask to join the class.
          </Alert>
          {selectedType && (
            <Stack gap="xs">
              <Text>
                <strong>Class Type:</strong> {selectedType.name}
              </Text>
              <Text>
                <strong>Sessions:</strong> {form.values.sessions.length}
              </Text>
              <Text>
                <strong>Total Price:</strong> €
                {calculateTotalPrice().toFixed(2)}
              </Text>
            </Stack>
          )}
        </Stack>
      ),
      labels: {
        confirm: "Confirm Assignment",
        cancel: "Cancel",
      },
      confirmProps: {
        loading: createSessionsMutation.isPending,
      },
      onConfirm: handleConfirmAssignment,
    });
  };
  if (!groupClassTypes) {
    return <FancyLoader />;
  }
  return (
    <Stack gap="md">
      <Alert color="blue" icon={<IconInfoCircle size={16} />}>
        Assign a group class type to this group to create sessions.
      </Alert>
      <Paper p="md" withBorder>
        <div>
          <Stack gap="md">
            <Select
              label="Group Class Type"
              placeholder="Select a class type"
              data={groupClassTypes.map((type) => ({
                value: type.id,
                label: `${type.name} (${type.sessionCount} sessions, €${(type.price / 100).toFixed(2)} ${type.pricePer === "SESSION" ? "per session" : "per course"})`,
              }))}
              {...form.getInputProps("groupClassTypeId")}
              onChange={(value) => value && handleTypeSelect(value)}
            />
            {selectedType && (
              <Button
                type="button"
                disabled={!form.values.groupClassTypeId}
                onClick={openAssignmentModal}
              >
                Assign Type & Create Sessions
              </Button>
            )}
          </Stack>
        </div>
      </Paper>
      {form.values.sessions.length > 0 && (
        <Paper p="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>
                Schedule Sessions ({form.values.sessions.length})
              </Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="outline"
                  leftSection={<IconWand size={14} />}
                  disabled={!firstScheduledAt}
                  onClick={handleScheduleWeekly}
                >
                  Schedule Weekly
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  leftSection={<IconPlus size={14} />}
                  onClick={handleAddSession}
                >
                  Add Session
                </Button>
              </Group>
            </Group>
            {form.values.sessions.map((session, index) => (
              <Group key={form.key(`sessions.${index}`)} align="flex-end">
                <DateTimePicker
                  label={`Session ${index + 1}`}
                  placeholder="Select date and time"
                  value={session.scheduledAt}
                  {...form.getInputProps(`sessions.${index}.scheduledAt`)}
                  style={{ flex: 1 }}
                  minDate={new Date()}
                />
                {form.values.sessions.length > 1 && (
                  <Button
                    size="xs"
                    color="red"
                    variant="outline"
                    onClick={() => handleRemoveSession(index)}
                  >
                    <IconTrash size={14} />
                  </Button>
                )}
              </Group>
            ))}
            <Paper p="sm" bg="blue.0">
              <Text fw={500} c="blue">
                Total Course Price: €{calculateTotalPrice().toFixed(2)}
              </Text>
              {selectedType && selectedType.pricePer === "SESSION" && (
                <Text size="xs" c="dimmed">
                  €{(selectedType.price / 100).toFixed(2)} ×{" "}
                  {form.values.sessions.length} sessions
                </Text>
              )}
            </Paper>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

const GroupSessions = ({
  group,
  refetch,
}: {
  group: GroupTrainerOutput;
  refetch: () => void;
}) => {
  const groupClassType = group.groupClassType;
  if (groupClassType) {
    return (
      <GroupSessionsDisplay group={group} groupClassType={groupClassType} />
    );
  } else {
    return <GroupSessionsAssignment group={group} refetch={refetch} />;
  }
};

export const GroupDetail = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const {
    data: group,
    isLoading,
    refetch,
  } = client.useQuery("get", "/trainer/groups/{groupId}", {
    params: { path: { groupId } },
  });

  const { data: groupClassTypes } = client.useQuery(
    "get",
    "/trainer/profile/group-class-types",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  const { activeTab, onTabChange } = useHashTab<string>("info");
  const { navigator } = useNavigator();
  const updateGroup = client.useMutation("put", "/trainer/groups/{groupId}");

  const handleStatusChange = async (newStatus: GroupStatus) => {
    await updateGroup.mutateAsync({
      params: { path: { groupId } },
      body: { status: newStatus },
    });
    refetch();
  };

  const handleChatToggle = async () => {
    if (!group) return;
    await updateGroup.mutateAsync({
      params: { path: { groupId } },
      body: { isChatEnabled: !group.isChatEnabled },
    });
    refetch();
  };

  if (isLoading || !group || !groupClassTypes) {
    return <FancyLoader />;
  }

  const invitationLink = `${window.location.origin}${createPath({ path: UniversalRoutes.GROUPS.JOIN, params: { groupId } })}`;

  // Get pending requests
  const pendingRequests = group.participants.filter(
    (participant) => participant.status === "REQUESTED",
  );

  return (
    <Stack>
      <Header title={group.name} />

      <Tabs value={activeTab} onChange={(value) => value && onTabChange(value)}>
        <Tabs.List grow>
          <Tabs.Tab value="info" leftSection={<IconInfoCircle size={16} />}>
            Info
          </Tabs.Tab>
          <Tabs.Tab value="participants" leftSection={<IconUsers size={16} />}>
            Participants
          </Tabs.Tab>
          <Tabs.Tab value="files" leftSection={<IconFile size={16} />}>
            Files
          </Tabs.Tab>
          <Tabs.Tab value="sessions" leftSection={<IconCalendar size={16} />}>
            Sessions
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="info" pt="md">
          <Stack gap="md">
            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Title order={5}>
                    Pending Requests ({pendingRequests.length})
                  </Title>
                  <Stack gap="md">
                    {pendingRequests.map((request) => (
                      <RequestCard
                        key={request.id}
                        request={request}
                        groupId={groupId}
                        refetch={refetch}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            )}

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Title order={5}>Description</Title>
                <Text>{group.description || "No description provided"}</Text>
                <Title order={5}>Invitation link</Title>
                <Anchor
                  href={invitationLink}
                  style={{
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                  }}
                >
                  {invitationLink}
                </Anchor>
                <Button
                  mt="xs"
                  variant="light"
                  leftSection={<IconShare size={16} />}
                  onClick={async () => {
                    try {
                      await window.navigator.share({
                        title: `Join my group ${group.name} on Woofapy!`,
                        text: `I would like to invite you to dog training group ${group.name} on Woofapy!`,
                        url: invitationLink,
                      });
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  Share link
                </Button>
              </Stack>
            </Paper>

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Title order={5}>Settings</Title>
                <Group gap="lg">
                  <div>
                    <Text size="sm" c="dimmed">
                      Max Participants
                    </Text>
                    <Text fw={500}>{group.maxParticipants}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">
                      Current Participants
                    </Text>
                    <Text fw={500}>{group.participantCount}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">
                      Status (you always approve new requests)
                    </Text>
                    <Badge
                      color={getGroupStatusColor(group.status)}
                      variant="light"
                    >
                      {getGroupStatusDisplayText(group.status)}
                    </Badge>
                  </div>
                </Group>
              </Stack>
            </Paper>

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Title order={5}>Actions</Title>
                <Group gap="md">
                  <Button
                    variant="outline"
                    onClick={() => {
                      modals.open({
                        title: "Edit Group",
                        children: (
                          <EditGroupForm
                            group={group}
                            onClose={() => modals.closeAll()}
                            onSuccess={() => {
                              modals.closeAll();
                              refetch();
                            }}
                          />
                        ),
                        size: "lg",
                      });
                    }}
                  >
                    Edit Group
                  </Button>
                  <Button
                    variant="outline"
                    leftSection={
                      group.isChatEnabled ? (
                        <IconMessageOff size={16} />
                      ) : (
                        <IconMessage size={16} />
                      )
                    }
                    onClick={handleChatToggle}
                    loading={updateGroup.isPending}
                  >
                    {group.isChatEnabled ? "Disable Chat" : "Enable Chat"}
                  </Button>
                  {group.isChatEnabled && (
                    <Button
                      variant="outline"
                      leftSection={<IconMessage size={16} />}
                      onClick={navigator({
                        path: UniversalRoutes.GROUP_CHAT.DETAIL,
                        params: { groupId },
                      })}
                    >
                      Send Message
                    </Button>
                  )}
                  <Menu shadow="md" width={150}>
                    <Menu.Target>
                      <Button
                        variant="outline"
                        rightSection={<IconChevronDown size={16} />}
                        loading={updateGroup.isPending}
                      >
                        Change Status
                      </Button>
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconLockOpen size={16} />}
                        onClick={() => handleStatusChange("OPEN")}
                        disabled={group.status === "OPEN"}
                      >
                        {getGroupStatusDisplayText("OPEN")}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconShare size={16} />}
                        onClick={() => handleStatusChange("OPEN_VIA_LINK")}
                        disabled={group.status === "OPEN_VIA_LINK"}
                      >
                        {getGroupStatusDisplayText("OPEN_VIA_LINK")}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconLock size={16} />}
                        onClick={() => handleStatusChange("LOCKED")}
                        disabled={group.status === "LOCKED"}
                      >
                        {getGroupStatusDisplayText("LOCKED")}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconMinus size={16} />}
                        onClick={() => handleStatusChange("INACTIVE")}
                        disabled={group.status === "INACTIVE"}
                      >
                        {getGroupStatusDisplayText("INACTIVE")}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="participants" pt="md">
          <GroupParticipants groupId={groupId} />
        </Tabs.Panel>

        <Tabs.Panel value="files" pt="md">
          <GroupFiles groupId={groupId} />
        </Tabs.Panel>

        <Tabs.Panel value="sessions" pt="md">
          <GroupSessions group={group} refetch={refetch} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export const PublicGroupDetail = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const groupDetailPath = createPath({
    path: UniversalRoutes.GROUPS.DETAIL,
    params: { groupId: groupId },
  });
  return <Redirect to={groupDetailPath} />;
};
